/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * What a page looked like when the visitor left a history entry — which rows
 * were open, how many "Toon meer" had revealed, the sort, a search term, the
 * compare picks — so Back, Forward, a reload and the in-page "terug" links
 * bring it back exactly as it was (Jasper, 2026-10-03). A FRESH navigation (a
 * header link, a hero CTA, a new visit) gets a new history entry and starts
 * clean, because there is nothing stored under its key.
 *
 * One record per history entry, keyed by React Router's `location.key`, with
 * the entry's address (`pathname + search`) and named slots: `comparer`
 * (ComparisonEngine), `overview` (the aanbiedersoverzicht) and
 * `charger_brands` (the brand hub: its search, filters and model finder). A
 * new per-page UI state joins as a slot of its own; a slot no component reads
 * any more goes on {@link RETIRED_VIEW_SLOTS}.
 *
 * Two stores, and the difference between them is the hydration rule:
 *
 * - The IN-MEMORY map (this document only) is the primary store, and the ONLY
 *   one a component may read while rendering (a useState initializer). A
 *   record can only be in it if this document's own code wrote it, after the
 *   component had mounted and the visitor had done something — so at the
 *   first render of a document (the hydrating one, which must equal the baked
 *   HTML) it is always empty, on the server and in the browser alike. That is
 *   what lets an in-tab Back render the restored page at its FIRST render, so
 *   the scroll manager's immediate restore lands on the final layout.
 * - The sessionStorage MIRROR exists only for a full document load onto an
 *   entry that had state: a reload, or a Back/Forward that crosses a document
 *   boundary. It is read only after mount (`takeFirstLoadSlot`, from an
 *   effect), only while the document is still on its first location, only
 *   when the load was a `reload` or `back_forward` (a `navigate` load is a new
 *   visit and starts clean), and only when the stored address equals the
 *   current one — the first entry of EVERY document has the key "default", so
 *   keys alone collide across documents in one tab.
 *
 * Nothing is written while a slot is at its default and no record exists for
 * the key, so an untouched homepage costs no storage at all. Every storage
 * access is in try/catch: a private window or a full quota only loses the
 * convenience.
 *
 * SSR-safe at module scope: nothing here touches `window`, `sessionStorage` or
 * `performance` until a function is called, and the bake never calls the
 * writers (they run in effects). Functional storage, the same class as the
 * scroll positions (components/ScrollToTop.tsx) — no consent implication.
 */

export const VIEW_STATE_STORAGE_KEY = 'ere:view-state';
/** Records kept, in memory and in the mirror (ScrollToTop keeps as many positions). */
const MAX_SAVED = 50;

export type ViewSlots = Record<string, unknown>;

interface ViewRecord {
  /** `pathname + search` of the entry the record belongs to. */
  path: string;
  slots: ViewSlots;
}

interface StoredRecord extends ViewRecord {
  key: string;
}

/** Load types that land on an existing history entry, so its stored state is its own. */
const RESTORING_LOADS = new Set(['reload', 'back_forward']);

/**
 * Slots no component reads any more. The mirror of a tab that ran an older
 * bundle can still hold one, and a first-load restore must not wait for it:
 * no component would ever apply it, so the scroll manager would hold the
 * saved position until its deadline and then give it up. Dropped when a load
 * reads the mirror ({@link noteViewStateLocation}); the next write of that
 * entry leaves it out. `mid_register`: the register on /mid-check
 * (MidRegister), retired 2026-10-21 — its model search lives in the brand
 * hub's `charger_brands` slot since.
 */
export const RETIRED_VIEW_SLOTS: ReadonlySet<string> = new Set(['mid_register']);

const withoutRetiredSlots = (slots: ViewSlots): ViewSlots =>
  Object.fromEntries(Object.entries(slots).filter(([slot]) => !RETIRED_VIEW_SLOTS.has(slot)));

// ─── Module state ─────────────────────────────────────────────────────────

const memory = new Map<string, ViewRecord>();
/** The key of the document's first location; null until it is noted. */
let documentKey: string | null = null;
/** Set once the document has been on another location: the first-load restore is over. */
let leftDocumentLocation = false;
/**
 * The record a full document load found in the mirror for its first location,
 * with the slots that still have to be applied (`pending`). Null: nothing to
 * restore on this load.
 */
let firstLoad: { key: string; path: string; slots: ViewSlots; pending: Set<string> } | null = null;

// ─── Storage helpers ──────────────────────────────────────────────────────

const isRecordShape = (value: unknown): value is StoredRecord => {
  if (!value || typeof value !== 'object') return false;
  const r = value as Partial<StoredRecord>;
  return (
    typeof r.key === 'string' &&
    typeof r.path === 'string' &&
    !!r.slots &&
    typeof r.slots === 'object' &&
    !Array.isArray(r.slots)
  );
};

/** The mirror, oldest first. An array, not an object: an all-digit key would sort first. */
const readStored = (): StoredRecord[] => {
  try {
    if (typeof window === 'undefined') return [];
    const raw = window.sessionStorage.getItem(VIEW_STATE_STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter(isRecordShape) : [];
  } catch {
    return [];
  }
};

const writeStored = (records: StoredRecord[]): void => {
  try {
    if (typeof window === 'undefined') return;
    const kept = records.slice(-MAX_SAVED);
    if (kept.length === 0) window.sessionStorage.removeItem(VIEW_STATE_STORAGE_KEY);
    else window.sessionStorage.setItem(VIEW_STATE_STORAGE_KEY, JSON.stringify(kept));
  } catch {
    /* private mode / quota — the restore is a convenience, not a contract */
  }
};

/** Replace (or, with null, drop) the mirror's record for `key`; the newest goes last. */
const mirror = (key: string, record: ViewRecord | null): void => {
  const records = readStored().filter((r) => r.key !== key);
  if (record) records.push({ key, path: record.path, slots: record.slots });
  writeStored(records);
};

const remember = (key: string, record: ViewRecord): void => {
  // Re-inserted, so the map's order is the order of last use and the cap
  // drops the entry left longest ago.
  memory.delete(key);
  memory.set(key, record);
  while (memory.size > MAX_SAVED) {
    const oldest = memory.keys().next().value;
    if (oldest === undefined) break;
    memory.delete(oldest);
  }
};

/**
 * How this document was loaded: `navigate`, `reload`, `back_forward`,
 * `prerender`, or null where the browser cannot say (or on the server).
 */
export const documentLoadType = (): string | null => {
  try {
    if (typeof performance === 'undefined') return null;
    const entries =
      typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')
        : [];
    const type = (entries[0] as PerformanceNavigationTiming | undefined)?.type;
    if (typeof type === 'string') return type;
    // Navigation Timing level 1, for a browser without the entry.
    const legacy = (performance as unknown as { navigation?: { type?: number } }).navigation?.type;
    if (legacy === 1) return 'reload';
    if (legacy === 2) return 'back_forward';
    if (legacy === 0) return 'navigate';
  } catch {
    /* nothing to learn */
  }
  return null;
};

// ─── The document's first location ───────────────────────────────────────

/**
 * Tell the store where the router is. The scroll manager calls it for every
 * location (it sees them all, first); a component using a slot calls it too,
 * which changes nothing once the first location is known. The first call
 * fixes the document's first location and looks in the mirror for a record
 * to restore on this load; a later call with another key ends that chance.
 */
export const noteViewStateLocation = (key: string, path: string): void => {
  if (documentKey === null) {
    documentKey = key;
    firstLoad = null;
    const loadType = documentLoadType();
    const stored = readStored().find((r) => r.key === key);
    if (!stored) return;
    if (loadType !== null && RESTORING_LOADS.has(loadType)) {
      const slots = withoutRetiredSlots(stored.slots);
      if (stored.path === path && Object.keys(slots).length > 0) {
        firstLoad = {
          key,
          path,
          slots,
          pending: new Set(Object.keys(slots)),
        };
      }
      return;
    }
    // A new visit on an entry key AND address the mirror already holds: the
    // "default" key of an earlier document in this tab, at the same address.
    // That record is not this entry's, and a later reload of this entry would
    // find it: drop it. (Under another address it cannot be mistaken for this
    // entry, and it may still be its own entry's, further back in the tab.)
    if (stored.path === path) mirror(key, null);
    return;
  }
  if (key !== documentKey) leftDocumentLocation = true;
};

const onDocumentLocation = (key: string): boolean => documentKey === key && !leftDocumentLocation;

// ─── Reading ──────────────────────────────────────────────────────────────

/**
 * The slot as this document left it for this entry, or undefined. The one
 * read that is safe while rendering (see the module comment).
 */
export const readViewSlot = (key: string, path: string, slot: string): unknown => {
  const record = memory.get(key);
  if (!record || record.path !== path) return undefined;
  return Object.prototype.hasOwnProperty.call(record.slots, slot) ? record.slots[slot] : undefined;
};

/**
 * After mount only: the slot a full document load found in the mirror for
 * this entry, while it still has to be applied — or undefined. The record is
 * moved into the in-memory map, so a later remount on this entry reads it at
 * its first render. Call `markFirstLoadApplied` once the restored state has
 * been committed.
 */
export const takeFirstLoadSlot = (key: string, path: string, slot: string): unknown => {
  if (!firstLoad || firstLoad.key !== key || firstLoad.path !== path) return undefined;
  if (!onDocumentLocation(key) || !firstLoad.pending.has(slot)) return undefined;
  if (!memory.has(key)) remember(key, { path, slots: { ...firstLoad.slots } });
  return firstLoad.slots[slot];
};

/** The slot's first-load restore has been committed (or there was nothing usable in it). */
export const markFirstLoadApplied = (key: string, slot: string): void => {
  if (firstLoad && firstLoad.key === key) firstLoad.pending.delete(slot);
};

/**
 * For the scroll manager: this entry was loaded with view state to restore,
 * so its saved scroll position belongs to the restored page, not the bare one.
 */
export const hasFirstLoadViewState = (key: string): boolean =>
  !!firstLoad && firstLoad.key === key && onDocumentLocation(key);

/** For the scroll manager: some slot of this entry is still waiting to be applied. */
export const firstLoadRestorePending = (key: string): boolean =>
  hasFirstLoadViewState(key) && (firstLoad?.pending.size ?? 0) > 0;

// ─── Writing ──────────────────────────────────────────────────────────────

/**
 * Save a slot under the entry the visitor is on. `isDefault`: the slot is at
 * its initial value — then nothing is written unless a record already holds
 * the slot, which is dropped (an entry back at its defaults needs no record).
 */
export const writeViewSlot = (
  key: string,
  path: string,
  slot: string,
  value: unknown,
  isDefault: boolean
): void => {
  const existing = memory.get(key);
  const record = existing && existing.path === path ? existing : null;
  if (!record) {
    if (isDefault) return;
    const next: ViewRecord = { path, slots: { [slot]: value } };
    remember(key, next);
    mirror(key, next);
    return;
  }
  const has = Object.prototype.hasOwnProperty.call(record.slots, slot);
  if (isDefault) {
    if (!has) return;
    const slots = { ...record.slots };
    delete slots[slot];
    if (Object.keys(slots).length === 0) {
      memory.delete(key);
      mirror(key, null);
      return;
    }
    const next = { path, slots };
    remember(key, next);
    mirror(key, next);
    return;
  }
  if (has && JSON.stringify(record.slots[slot]) === JSON.stringify(value)) return;
  const next = { path, slots: { ...record.slots, [slot]: value } };
  remember(key, next);
  mirror(key, next);
};

/** Tests only: forget the document, both stores' contents in memory and the mirror. */
export const resetViewStateForTests = (): void => {
  memory.clear();
  documentKey = null;
  leftDocumentLocation = false;
  firstLoad = null;
  try {
    if (typeof window !== 'undefined') window.sessionStorage.removeItem(VIEW_STATE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
